import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle,
  ClipboardList,
  FileCheck,
  GraduationCap,
  History,
  Shield,
} from 'lucide-react';

const complianceAreas = [
  {
    icon: BadgeCheck,
    title: 'Recruitment Checks',
    description: 'Identity verification, right to work, employment history and gap analysis for every candidate.',
  },
  {
    icon: Shield,
    title: 'DBS & References',
    description: 'Enhanced DBS checks and reference verification tracked before readiness approval.',
  },
  {
    icon: GraduationCap,
    title: 'Training & Induction',
    description: 'Mandatory training completion tracked and certificates stored before active work.',
  },
  {
    icon: FileCheck,
    title: 'Policy Acknowledgement',
    description: 'Key policies issued and acknowledged, with timestamps and versions recorded.',
  },
  {
    icon: ClipboardList,
    title: 'Document Management',
    description: 'Compliance documents stored digitally with expiry tracking and review status.',
  },
  {
    icon: History,
    title: 'Audit Trail',
    description: 'A log of compliance actions, uploads and approvals for easier review.',
  },
];

const checklistItems = [
  'Application form and employment history',
  'Proof of identity and right to work',
  'References from recent employers',
  'Enhanced DBS certificate or update service',
  'Health declaration and screening',
  'Mandatory training certificates',
  'Induction completion and shadowing',
  'Policy acknowledgements',
  'Contract and handbook receipt',
];

export default function CompliancePage() {
  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-6">
              CareOps Compliance Workflows
            </h1>
            <p className="text-lg text-text-muted leading-relaxed">
              Give care teams a clearer way to track applicant readiness, worker evidence, document reviews, training, DBS checks and audit history.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary mb-4">
              What Essential includes
            </h2>
            <p className="text-lg text-text-muted">
              The basic subscription keeps the core onboarding and check-completion workflow available before providers add premium controls.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceAreas.map((area, idx) => (
              <div key={area.title} className="card-public" data-testid={`compliance-card-${idx}`}>
                <div className="w-14 h-14 bg-accent rounded-2xl flex items-center justify-center mb-6">
                  <area.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">{area.title}</h3>
                <p className="text-text-muted">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary mb-6">
                Clear records before approval
              </h2>
              <p className="text-lg text-text-muted mb-8">
                Before a worker is treated as ready, teams can see whether required checks, documents and training are complete and in order.
              </p>

              <div className="bg-[#F8FAFA] rounded-2xl p-6 border border-[#E4E8EB]">
                <h4 className="font-heading font-medium text-text-primary mb-4">Core readiness items:</h4>
                <ul className="space-y-3">
                  {checklistItems.map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                      <span className="text-text-primary text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7511751/pexels-photo-7511751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Organised compliance records"
                className="relative rounded-3xl shadow-lg w-full object-cover aspect-square"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl border border-[#E4E8EB] p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-3xl font-semibold text-text-primary mb-6">
                  Records you can rely on
                </h2>
                <p className="text-text-muted mb-6">
                  CareOps keeps core worker evidence organised so managers can review blockers, approvals, expiry dates and audit history from the portal.
                </p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-lg text-text-muted mb-6">
                  Ready to request CareOps access?
                </p>
                <Button asChild className="bg-primary hover:bg-primary-hover text-white rounded-full" data-testid="contact-compliance-btn">
                  <Link to="/contact?type=platform_demo">
                    Request Invite
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
